import { useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Alert, Snackbar } from "@mui/material";
import { Link } from "react-router-dom";
import Loader from "../../../admin/components/Loader";
import { authData } from "../../../admin/components/GetAuth";
import styles from "./track-booking.module.css";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";

function TrackBooking() {
  const auth = authData();

  const [loading, setLoading] = useState(false);
  const [bookingPhone, setBookingPhone] = useState("");
  const [bookingId, setBookingId] = useState("");
  const [bookingData, setBookingData] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");

  const { websiteData, loading: wLoading } = useWebsite();

  useSiteBranding("Track you booking", websiteData);

  const showSnackbar = (message, type = "error") => {
    setSnackbarOpen(true);
    setSnackbarMessage(message);
    setSnackbarType(type);
  };

  const searchBooking = async () => {
    setBookingData(null);

    if (!bookingPhone.trim()) {
      showSnackbar("Please enter phone number");
      return;
    }

    if (!bookingId.trim()) {
      showSnackbar("Please enter booking ID");
      return;
    }

    try {
      setLoading(true);

      const response = await axios.post(process.env.REACT_APP_API_URL + "v1/tarck-booking-details", { phone: bookingPhone, bookId: bookingId, utoken: websiteData ? websiteData?.utoken : '' }, {
        headers: {
          Authorization: `Bearer ${auth?.api_token}`,
        }
      });

      if (response?.data?.status === "ERROR") {
        showSnackbar(response?.data?.message || "Booking not found", "error");
      } else {
        setBookingData(response?.data?.data || null);
      }
    } catch (error) {
      showSnackbar("Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const bookingStatus = parseInt(bookingData?.status);

  const getStatusLabel = () => {
    if (bookingStatus === -1) return "CANCELLED";
    if (bookingStatus === 0 || bookingStatus === 1) return "BOOKED";
    if (bookingStatus === 2 || bookingStatus === 3) return "PICKED UP";
    if (bookingStatus === 4 || bookingStatus === 5 || bookingStatus === 6) return "IN TRANSIT";
    if (bookingStatus === 8) return "PARTIALLY DELIVERED";
    if (bookingStatus === 7) return "DELIVERED";
    return "PENDING";
  };

  const getStatusClass = () => {
    if (bookingStatus === -1) return styles.StatusCancelled;
    if (bookingStatus === 7) return styles.StatusDelivered;
    if (bookingStatus === 8) return styles.StatusPartial;
    if (bookingStatus >= 4 && bookingStatus <= 6) return styles.StatusTransit;
    return styles.StatusBooked;
  };

  const deliveryDate = bookingData?.estimated_delivery_date ? dayjs(bookingData.estimated_delivery_date).format("ddd, MMM DD YYYY") : bookingData?.booking_date ? dayjs(bookingData.booking_date).add(1, "month").format("ddd, MMM DD YYYY") : "-";

  const themeVars = useMemo(() => {
    return {
      "--primary-color": websiteData?.home_config?.primaryColor || "#0a2a52",
      "--secondary-color": websiteData?.home_config?.secondaryColor || "#ef233c",

      // optional derived shades
      "--primary-light": `${websiteData?.home_config?.primaryColor || "#0a2a52"}15`,
      "--secondary-light": `${websiteData?.home_config?.secondaryColor || "#ef233c"}15`,
    };
  }, [websiteData]);

  const getItemStatusInfo = (item) => {
    if (parseInt(item?.is_deliverred) === 1) {
      return { label: item?.status_text || "Delivered", className: styles.ItemStatusDelivered };
    }
    if (parseInt(item?.is_out_for_delivery) === 1) {
      return { label: item?.status_text || "Out For Delivery", className: styles.ItemStatusTransit };
    }
    if (parseInt(item?.is_ship_received) === 1) {
      return { label: item?.status_text || "Received", className: styles.ItemStatusReceived };
    }
    return { label: item?.status_text || bookingData?.status_text || "-", className: styles.ItemStatusPending };
  };

  return (<>
    {wLoading && <Loader />}
    <Loader open={loading} setOpen={setLoading} />

    <div className={styles.TrackPage} style={themeVars}>
      <div className={styles.TrackWrapper}>
        <div className={styles.BrandHero}>
          <div className={styles.HeroTopBar}>
            <Link to="/" className={styles.BackHomeBtn}>← Back to Home</Link>
            <div className={styles.HeroChip}>Live Shipment Tracking</div>
          </div>

          <div className={styles.BrandHeroContent}>
            <div className={styles.BrandLeft}>
              <div>
                <img src={websiteData && websiteData?.home_config && websiteData?.home_config?.logoURL ? websiteData?.home_config?.logoURL : "/images/logo.png"} alt={websiteData && websiteData?.home_config && websiteData?.home_config?.companyName ? websiteData?.home_config?.companyName : 'EF LOGISTICS'} className={styles.BrandLogo} />
              </div>

              <div className={styles.BrandText}>
                <h2>{websiteData?.company_name || "EF LOGISTICS"}</h2>
                <p>{websiteData?.home_config?.heroTitle || "Seamless logistics from pickup to final delivery."}</p>
              </div>
            </div>

            <div className={styles.BrandRight}>
              <h1>Track Your Booking</h1>
              <p>Enter your booking ID and registered phone number to check shipment progress, delivery estimate, and order item status in real time.</p>
            </div>
          </div>
        </div>

        {/* SEARCH CARD */}
        <div className={styles.SearchCard}>
          <div className={styles.SearchHeader}>
            <h3>Find Your Shipment</h3>
            <p>Use the booking ID and phone number linked to your order.</p>
          </div>

          <div className={styles.SearchGrid}>
            <div className={styles.InputGroup}>
              <label>Phone Number</label>
              <input type="text" placeholder="Enter phone number" value={bookingPhone} onChange={(e) => setBookingPhone(e.target.value)} />
            </div>

            <div className={styles.InputGroup}>
              <label>Booking ID</label>
              <input type="text" placeholder="Enter booking ID" value={bookingId} onChange={(e) => setBookingId(e.target.value)} />
            </div>

            <div className={styles.ButtonWrap}>
              <button className={styles.SearchBtn} onClick={searchBooking}>Track Shipment</button>
            </div>
          </div>
        </div>

        {!bookingData && (<div className={styles.EmptyState}>
          <div className={styles.EmptyStateIcon}>📦</div>
          <h3>Track your shipment in one place</h3>
          <p>Check order status, shipment movement, delivery estimate, and item details by entering your booking information above.</p>
        </div>)}

        {bookingData && (<>
          <div className={styles.SummaryCard}>
            <div className={styles.SummaryTop}>
              <div>
                <p className={styles.SectionMiniTitle}>Shipment Summary</p>
                <h3>{bookingData?.booking_id || "Booking Details"}</h3>
              </div>

              <span className={`${styles.StatusBadge} ${getStatusClass()}`}>
                {getStatusLabel()}
              </span>
            </div>

            <div className={styles.SummaryGrid}>
              <div className={styles.SummaryBox}>
                <span>Booking ID</span>
                <strong>{bookingData?.booking_id || "-"}</strong>
              </div>

              <div className={styles.SummaryBox}>
                <span>Order Placed On</span>
                <strong>
                  {bookingData?.created_at
                    ? dayjs(bookingData.created_at).format("DD MMM YYYY")
                    : "-"}
                </strong>
              </div>

              <div className={styles.SummaryBox}>
                <span>Contact Number</span>
                <strong>{bookingData?.pickup_details?.phone || "-"}</strong>
              </div>

              <div className={styles.SummaryBox}>
                <span>
                  {bookingStatus === 7 ? "Delivered At" : "Estimated Delivery"}
                </span>
                <strong>{deliveryDate}</strong>
              </div>
            </div>
          </div>

          {/* DETAILS GRID */}
          <div className={styles.ResultGrid}>
            {/* LEFT: TIMELINE */}
            <div className={styles.ResultCard}>
              <div className={styles.CardHeader}>
                <div>
                  <p className={styles.SectionMiniTitle}>Tracking Updates</p>
                  <h3>Shipment Timeline</h3>
                </div>
              </div>

              {bookingData?.trackingDetails?.length > 0 ? (
                <ul className={styles.Timeline}>
                  {bookingData.trackingDetails.map((item, index) => {
                    const completed = !!item?.is_completed;
                    return (
                      <li
                        key={index}
                        className={`${styles.TimelineItem} ${completed ? styles.Completed : ""
                          }`}
                      >
                        <div className={styles.TimelineDot}></div>
                        <div className={styles.TimelineContent}>
                          <p>{item?.status_text || "-"}</p>
                          {item?.datetime ? (
                            <span>
                              {dayjs(item.datetime).format("DD MMM YYYY LT")}
                            </span>
                          ) : (
                            <span>Pending update</span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className={styles.NoData}>No tracking updates available.</div>
              )}
            </div>

            {/* RIGHT: DELIVERY / ORDER INFO */}
            <div className={styles.ResultCard}>
              <div className={styles.CardHeader}>
                <div>
                  <p className={styles.SectionMiniTitle}>Delivery Information</p>
                  <h3>Order Details</h3>
                </div>
              </div>

              <div className={styles.HighlightBox}>
                <span>
                  {bookingStatus === 7 ? "Delivered On" : "Estimated Delivery Date"}
                </span>
                <h2>{deliveryDate}</h2>
                <p>Current shipment status: {getStatusLabel()}</p>
              </div>

              <div className={styles.InfoGrid}>
                <div className={styles.InfoBox}>
                  <span>Booking Reference</span>
                  <strong>{bookingData?.booking_id || "-"}</strong>
                </div>

                <div className={styles.InfoBox}>
                  <span>Customer Phone</span>
                  <strong>{bookingData?.pickup_details?.phone || "-"}</strong>
                </div>

                <div className={styles.InfoBox}>
                  <span>Created Date</span>
                  <strong>
                    {bookingData?.created_at
                      ? dayjs(bookingData.created_at).format("DD MMM YYYY")
                      : "-"}
                  </strong>
                </div>

                <div className={styles.InfoBox}>
                  <span>Shipment Status</span>
                  <strong>{getStatusLabel()}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* PRODUCT TABLE */}
          {bookingData?.products?.length > 0 && (
            <div className={styles.ProductCard}>
              <div className={styles.CardHeader}>
                <div>
                  <p className={styles.SectionMiniTitle}>Booked Items</p>
                  <h3>Shipment Items</h3>
                </div>
              </div>

              <div className={styles.TableWrap}>
                <table className={styles.ProductTable}>
                  <thead>
                    <tr>
                      <th>Pickup ID</th>
                      <th>Item</th>
                      <th>Fragile</th>
                      <th>Estimated Value</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookingData.products.map((item, index) => {
                      const itemStatus = getItemStatusInfo(item);
                      return (
                        <tr key={index}>
                          <td>{item?.item_id || "-"}</td>
                          <td>{item?.product_title || "-"}</td>
                          <td>{item?.is_fragile ? "Yes" : "No"}</td>
                          <td>
                            £{parseFloat(item?.subtotal)?.toFixed(2)}
                            {parseFloat(item.discount) > 0 && (
                              <>
                                <br />
                                <span style={{ fontSize: "11px" }}>(-£{parseFloat(item.discount).toFixed(2)})</span>
                              </>
                            )}
                          </td>
                          <td>
                            <span className={`${styles.ItemStatusBadge} ${itemStatus.className}`}>{itemStatus.label}</span>
                            {parseInt(item?.is_deliverred) === 1 && item?.delivered_date && (
                              <span className={styles.ItemStatusDate}>{dayjs(item.delivered_date).format("DD MMM YYYY")}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {bookingData?.delivery_completions?.length > 0 && (
            <div className={`${styles.ProductCard} ${styles.DeliveryRecordCard}`}>
              <div className={styles.CardHeader}>
                <div>
                  <p className={styles.SectionMiniTitle}>Delivery Record</p>
                  <h3>Delivery Groups</h3>
                </div>
              </div>

              <div className={styles.DeliveryGroupList}>
                {bookingData.delivery_completions.map((group) => (
                  <div className={styles.DeliveryGroupBox} key={group.id}>
                    <div className={styles.DeliveryGroupTop}>
                      <span className={styles.DeliveryGroupDate}>
                        {dayjs(group.delivered_date).format("ddd, DD MMM YYYY")}
                      </span>
                      <span className={styles.DeliveryGroupCount}>
                        {group.items?.length || 0} item{(group.items?.length || 0) !== 1 ? "s" : ""} delivered
                      </span>
                    </div>

                    {group.items?.length > 0 && (
                      <ul className={styles.DeliveryGroupItems}>
                        {group.items.map((item) => (
                          <li key={item.id}>{item?.product_title || item?.item_id}</li>
                        ))}
                      </ul>
                    )}

                    {group.delivery_note && (
                      <p className={styles.DeliveryGroupNote}>"{group.delivery_note}"</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
        )}
      </div>
    </div>

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={5000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        onClose={() => setSnackbarOpen(false)}
        severity={snackbarType}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
  </>
  );
}

export default TrackBooking;